import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';

/**
 * Onglets multi-collections pour la section product-list.
 *
 * Au clic d'un onglet, on rend la section pour l'URL de la collection
 * (`/collections/{handle}?section_id=…`) via l'API Section Rendering, puis on
 * remplace la/les grille(s) `.resource-list`. La section source ses produits
 * depuis le `collection` contextuel quand des onglets sont présents.
 *
 * @extends {Component}
 */
class CollectionTabsComponent extends Component {
  #abort = new AbortController();
  #loading = false;
  /** @type {string | undefined} */
  #handle;
  #sort = 'popularity';
  /** @type {Set<string>} */
  #prefetched = new Set();

  connectedCallback() {
    super.connectedCallback();
    const { signal } = this.#abort;
    this.addEventListener('click', this.#onClick, { signal });
    // Préchargement à l'intention : survol d'un onglet → on chauffe son cache.
    this.addEventListener('pointerover', this.#onHover, { signal });

    const sortSelect = /** @type {HTMLSelectElement | null} */ (this.querySelector('[data-collection-sort]'));
    sortSelect?.addEventListener('change', this.#onSortChange, { signal });
    // Tri initial piloté par l'option sélectionnée dans le markup (ex. « Promos »).
    if (sortSelect) this.#sort = sortSelect.value;

    // Active le 1er onglet si aucun ne l'est (le markup est rendu par les blocs).
    const tabs = Array.from(this.querySelectorAll('.collection-tabs__tab'));
    const active = this.querySelector('.collection-tabs__tab.is-active') || tabs[0];
    if (active instanceof HTMLElement) {
      this.#setActive(active);
      this.#handle = active.dataset.collection;
    }

    // Applique le tri/filtre initial sur les cartes rendues côté serveur.
    this.#applySort();

    // Préchargement à l'idle : on chauffe les autres collections en arrière-plan,
    // sans alourdir le chargement initial → 1er switch instantané.
    const idle = window.requestIdleCallback || ((cb) => setTimeout(cb, 200));
    idle(() => tabs.forEach((tab) => this.#prefetch(tab.dataset.collection)));
  }

  /** @param {PointerEvent} event */
  #onHover = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const tab = target.closest?.('.collection-tabs__tab');
    if (tab instanceof HTMLElement) this.#prefetch(tab.dataset.collection);
  };

  /**
   * Met en cache le HTML rendu d'une collection (sans rien afficher) pour que le
   * clic suivant soit instantané.
   * @param {string | undefined} handle
   */
  #prefetch(handle) {
    const sectionId = this.dataset.sectionId;
    if (!handle || !sectionId || this.#prefetched.has(handle)) return;
    this.#prefetched.add(handle);
    const root = window.Shopify?.routes?.root || '/';
    const url = new URL(`${root}collections/${handle}`, window.location.origin);
    sectionRenderer.getSectionHTML(sectionId, true, url).catch(() => this.#prefetched.delete(handle));
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort.abort();
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const tab = target.closest('.collection-tabs__tab');
    if (!(tab instanceof HTMLElement) || tab.getAttribute('aria-selected') === 'true' || this.#loading) return;
    this.#setActive(tab);
    this.#handle = tab.dataset.collection;
    this.#load();
  };

  /** @param {Event} event */
  #onSortChange = (event) => {
    this.#sort = /** @type {HTMLSelectElement} */ (event.target).value;
    this.#applySort();
  };

  /**
   * Tri/filtre côté client sur les cartes chargées (instantané, comme la section
   * carrousel). Réordonne via `order` (compatible grille ET slideshow) et filtre
   * « Promos » en masquant les produits sans prix barré.
   */
  #applySort() {
    const items = Array.from(this.querySelectorAll('.resource-list__item'));
    if (!items.length) return;

    const num = (el, key) => parseFloat(el.dataset[key] || '0') || 0;
    /** @type {Record<string, (a: HTMLElement, b: HTMLElement) => number>} */
    const sorters = {
      'price-asc': (a, b) => num(a, 'price') - num(b, 'price'),
      'price-desc': (a, b) => num(b, 'price') - num(a, 'price'),
      'cbd-desc': (a, b) => num(b, 'cbd') - num(a, 'cbd'),
      newest: (a, b) => (b.dataset.date || '').localeCompare(a.dataset.date || ''),
    };
    const sorter = sorters[this.#sort];
    // Filtre « Promos » uniquement si la collection contient au moins un produit en
    // promo, sinon on masquerait tout (ex. catégorie Accessoires sans aucune promo).
    const promoOnly = this.#sort === 'promo' && items.some((item) => item.dataset.onsale === '1');
    const ordered = sorter ? items.slice().sort(sorter) : items;

    ordered.forEach((item, i) => {
      // Unité réordonnable : la slide en layout carrousel, sinon l'item lui-même.
      const unit = /** @type {HTMLElement} */ (item.closest('.resource-list__slide') || item);
      unit.style.order = sorter ? String(i) : '';
      unit.style.display = promoOnly && item.dataset.onsale !== '1' ? 'none' : '';
    });
  }

  async #load() {
    const handle = this.#handle;
    const sectionId = this.dataset.sectionId;
    if (!handle || !sectionId) return;

    this.#loading = true;

    const grids = Array.from(this.querySelectorAll('.resource-list'));
    // Fige la hauteur le temps du swap pour éviter tout saut de mise en page.
    grids.forEach((grid) => {
      grid.style.minHeight = `${grid.offsetHeight}px`;
      grid.classList.add('is-loading');
    });

    try {
      const root = window.Shopify?.routes?.root || '/';
      const url = new URL(`${root}collections/${handle}`, window.location.origin);

      // Délai mini identique à la section carrousel (80 ms) pour un dip court,
      // en parallèle du chargement réseau.
      const [html] = await Promise.all([
        sectionRenderer.getSectionHTML(sectionId, true, url),
        new Promise((resolve) => setTimeout(resolve, 80)),
      ]);
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = doc.querySelectorAll('.resource-list');

      // Même conteneur conservé → la transition d'opacité reste continue (pas de
      // remplacement de nœud). On ne remplace que le contenu interne.
      grids.forEach((grid, i) => {
        if (fresh[i]) grid.innerHTML = fresh[i].innerHTML;
      });

      // Ré-applique le tri courant sur les nouvelles cartes.
      this.#applySort();

      // Met à jour le compteur de produits depuis la réponse.
      const freshCount = doc.querySelector('[data-collection-count]');
      const currentCount = this.querySelector('[data-collection-count]');
      if (freshCount && currentCount) currentCount.textContent = freshCount.textContent;

      // Fondu d'entrée du nouveau contenu, puis on libère la hauteur.
      requestAnimationFrame(() => {
        grids.forEach((grid) => {
          grid.classList.remove('is-loading');
          grid.style.minHeight = '';
        });
      });
    } catch (error) {
      console.error('[collection-tabs]', error);
      grids.forEach((grid) => {
        grid.classList.remove('is-loading');
        grid.style.minHeight = '';
      });
    } finally {
      this.#loading = false;
    }
  }

  /** @param {HTMLElement} activeTab */
  #setActive(activeTab) {
    for (const tab of this.querySelectorAll('.collection-tabs__tab')) {
      const isActive = tab === activeTab;
      tab.classList.toggle('is-active', isActive);
      tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
    }
  }
}

if (!customElements.get('collection-tabs-component')) {
  customElements.define('collection-tabs-component', CollectionTabsComponent);
}
